import { useEffect, useState } from "react";
import { useParams } from "react-router";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { backendBase } from "@/api/backendBase";

interface GenerateReportDebugResponse {
  csvFile: string;
  sourceRowCounts: Record<string, number>;
  groundTruthOrders: number;
  matchedOrders: number;
  missingOrders: number;
  missingOrderSamples: string[];
}

interface ReconciliationReportResponse {
  name: string;
  total: number;
  rows: Record<string, string>[];
}

type MuiColor = "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";

const DISPLAY_COLUMNS = [
  "Order #",
  "Order Type",
  "Cin7 Status",
  "TOE Status",
  "3PL Status",
  "Expected Status (Truth)",
  "Status",
  "Rule ID",
  "Calc Status (Engine)",
];

const splitList = (value: string | undefined): string[] => (value ?? "").split(",").map((s) => s.trim());

export const ReportDebug = () => {
  const params = useParams();
  const date = params.date;

  const [summary, setSummary] = useState<GenerateReportDebugResponse | null>(null);
  const [report, setReport] = useState<ReconciliationReportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!date) return;
    setSummary(null);
    setReport(null);
    setError(null);
    fetch(`${backendBase()}/sandbox/generate-report-debug?date=${date}`)
      .then((res) => res.json())
      .then((json) => {
        if (json.error) {
          setError(json.error);
          return;
        }
        setSummary(json);
        return fetch(`${backendBase()}/sandbox/reconciliation-report/${json.csvFile}`)
          .then((res) => res.json())
          .then((reportJson) => {
            if (reportJson.error) {
              setError(reportJson.error);
              return;
            }
            setReport(reportJson);
          });
      })
      .catch((e) => setError(String(e)));
  }, [date]);

  if (!date) {
    return <Alert severity="error">Missing date in URL — expected /sandbox/report/debug/&lt;date&gt;</Alert>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!summary || !report) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mt: 4,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const rowColor = (row: Record<string, string>): MuiColor => {
    const calcStatuses = splitList(row["Calc Status (Engine)"]);
    if (new Set(calcStatuses).size > 1) return "warning";
    if (calcStatuses.includes(row["Expected Status (Truth)"])) return "success";
    return "default";
  };

  return (
    <Box
      sx={{
        p: 3,
        maxWidth: 1400,
        mx: "auto",
      }}
    >
      <Typography variant="h4" gutterBottom>
        Rules Engine Debug — {date}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        CSV file: {summary.csvFile}. Every row here is an Order # taken from the ground-truth fixture — all other columns
        are OUR OWN computed values (never copied from the fixture), so this is meant for eyeballing where the 29-rule
        table gets a specific known order wrong. "Expected Status (Truth)" is the fixture's own Status column for
        context only — its vocabulary doesn't include "Issue" at all, so it's not a pass/fail target, just a reference
        point. "Status" is the engine's actual (first-match-wins) decision; "Rule ID" / "Calc Status (Engine)" instead
        list EVERY rule that matched, comma-separated in rule-table order (e.g. "R1, R4, R7") — a row highlighted
        orange has matched rules that disagree on status, a priority conflict worth reviewing.
      </Typography>

      <Stack
        direction="row"
        spacing={3}
        sx={{ mb: 4 }}
      >
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography
            variant="overline"
            color="text.secondary"
          >
            Ground Truth Orders
          </Typography>
          <Typography variant="h5">{summary.groundTruthOrders}</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography
            variant="overline"
            color="text.secondary"
          >
            Matched (shown below)
          </Typography>
          <Typography variant="h5">{summary.matchedOrders}</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography
            variant="overline"
            color="text.secondary"
          >
            Missing (no SaleOrderDetails line)
          </Typography>
          <Typography variant="h5">{summary.missingOrders}</Typography>
          {summary.missingOrders > 0 && (
            <Chip
              label="can't be computed"
              color="warning"
              size="small"
              sx={{ mt: 1 }}
            />
          )}
        </Paper>
      </Stack>

      {summary.missingOrders > 0 && (
        <Alert
          severity="warning"
          variant="outlined"
          sx={{ mb: 4 }}
        >
          {summary.missingOrders} ground-truth Order #s have no matching line in this date's SaleOrderDetails export at
          all, so nothing could be computed for them: {summary.missingOrderSamples.slice(0, 15).join(", ")}
          {summary.missingOrders > 15 ? ", …" : ""}
        </Alert>
      )}

      <Typography
        variant="h6"
        gutterBottom
      >
        Per-order debug ({report.total})
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {DISPLAY_COLUMNS.map((col) => (
                <TableCell key={col}>{col}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {report.rows.map((row, i) => (
              <TableRow key={row["Order #"] ?? i}>
                {DISPLAY_COLUMNS.map((col) => (
                  <TableCell key={col}>
                    {col === "Calc Status (Engine)" ? (
                      <Chip
                        label={row[col]}
                        color={rowColor(row)}
                        size="small"
                      />
                    ) : (
                      row[col]
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
